import { ChangeDetectionStrategy, Component, computed, input, output, type InputSignal, type OutputEmitterRef, type Signal } from '@angular/core';
import type {
  WishlistCreateFieldErrors,
  WishlistCreateItemDraft,
  WishlistCreateItemPriority,
} from '@nexus/customer/wishlists/model';
import { WISHLIST_CREATE_PRIORITY_LABELS } from '@nexus/customer/wishlists/util';
import {
  UiActionCardComponent,
  UiButtonComponent,
  UiCardComponent,
  UiChipComponent,
  UiIconButtonComponent,
  UiInputComponent,
  UiLibraryIconComponent,
  UiTextareaComponent,
} from '@nexus/frontend-shared-ui';

interface PriorityOption {
  readonly id: WishlistCreateItemPriority;
  readonly label: string;
}

@Component({
  selector: 'wishlist-create-items-step',
  imports: [
    UiActionCardComponent,
    UiButtonComponent,
    UiCardComponent,
    UiChipComponent,
    UiIconButtonComponent,
    UiInputComponent,
    UiLibraryIconComponent,
    UiTextareaComponent,
  ],
  templateUrl: './wishlist-create-items-step.component.html',
  styleUrl: './wishlist-create-items-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistCreateItemsStepComponent {
  protected readonly priorityLabels: Readonly<Record<WishlistCreateItemPriority, string>> =
    WISHLIST_CREATE_PRIORITY_LABELS;

  public readonly items: InputSignal<readonly WishlistCreateItemDraft[]> =
    input.required<readonly WishlistCreateItemDraft[]>();
  public readonly itemDraft: InputSignal<WishlistCreateItemDraft> = input.required<WishlistCreateItemDraft>();
  public readonly itemDraftOpen: InputSignal<boolean> = input<boolean>(false);
  public readonly itemDraftErrors: InputSignal<WishlistCreateFieldErrors> =
    input.required<WishlistCreateFieldErrors>();
  public readonly itemDraftSubmitDisabled: InputSignal<boolean> = input<boolean>(false);
  public readonly itemDraftOpenChange: OutputEmitterRef<boolean> = output<boolean>();
  public readonly itemTitleChange: OutputEmitterRef<string> = output<string>();
  public readonly itemDescriptionChange: OutputEmitterRef<string> = output<string>();
  public readonly itemStoreUrlChange: OutputEmitterRef<string> = output<string>();
  public readonly itemPriceAmountChange: OutputEmitterRef<string> = output<string>();
  public readonly itemPriorityChange: OutputEmitterRef<WishlistCreateItemPriority> =
    output<WishlistCreateItemPriority>();
  public readonly itemPrivateNoteChange: OutputEmitterRef<string> = output<string>();
  public readonly itemAddSelected: OutputEmitterRef<void> = output<void>();
  public readonly itemRemoveSelected: OutputEmitterRef<string> = output<string>();

  protected readonly priorityOptions: Signal<readonly PriorityOption[]> = computed(
    (): readonly PriorityOption[] =>
      (['low', 'medium', 'high'] as const).map(
        (priority: WishlistCreateItemPriority): PriorityOption => ({
          id: priority,
          label: WISHLIST_CREATE_PRIORITY_LABELS[priority],
        }),
      ),
  );

  protected handleItemDraftOpenSelected(): void {
    this.itemDraftOpenChange.emit(true);
  }

  protected handleItemDraftCloseSelected(): void {
    this.itemDraftOpenChange.emit(false);
  }

  protected handleItemTitleChange(value: string): void {
    this.itemTitleChange.emit(value);
  }

  protected handleItemDescriptionChange(value: string): void {
    this.itemDescriptionChange.emit(value);
  }

  protected handleItemStoreUrlChange(value: string): void {
    this.itemStoreUrlChange.emit(value);
  }

  protected handleItemPriceAmountChange(value: string): void {
    this.itemPriceAmountChange.emit(value);
  }

  protected handleItemPriorityChange(priority: WishlistCreateItemPriority): void {
    this.itemPriorityChange.emit(priority);
  }

  protected handleItemPrivateNoteChange(value: string): void {
    this.itemPrivateNoteChange.emit(value);
  }

  protected handleItemAddSelected(): void {
    this.itemAddSelected.emit();
  }

  protected handleItemRemoveSelected(itemId: string): void {
    this.itemRemoveSelected.emit(itemId);
  }
}
